import React, { useState } from 'react';
import { X, Building2, CheckCircle } from './CustomIcons';

const COMMITMENT_OPTIONS = [
  { value: 'federal_bonding', label: 'Federal Bonding participant' },
  { value: 'wotc', label: 'WOTC-eligible employer' },
  { value: 'fair_chance', label: 'Fair Chance pledge signatory' },
  { value: 'open_to_all', label: 'Open to all' },
];

const CREDENTIAL_TYPES = ['Trades', 'Healthcare', 'Technology', 'Any'];

export default function EmployerOnboardingModal({ onComplete, onDismiss }) {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    org_name: '',
    commitment_level: '',
    preferred_credentials: [],
    contact_name: '',
    contact_email: '',
  });

  const toggleCred = (c) =>
    setForm(f => ({
      ...f,
      preferred_credentials: f.preferred_credentials.includes(c)
        ? f.preferred_credentials.filter(x => x !== c)
        : [...f.preferred_credentials, c],
    }));

  const canNext1 = form.org_name.trim().length > 0;
  const canNext2 = form.commitment_level.length > 0;
  const canNext3 = form.preferred_credentials.length > 0;
  const canSubmit = form.contact_name.trim().length > 0 && /\S+@\S+/.test(form.contact_email);

  const handleSubmit = async () => {
    setSubmitting(true);
    await onComplete(form);
    setSubmitting(false);
  };

  const inputStyle = {
    width: '100%',
    border: '1px solid #E2E8F0',
    borderRadius: '10px',
    padding: '11px 14px',
    fontSize: '14px',
    color: '#0F2744',
    outline: 'none',
    fontFamily: 'Inter, sans-serif',
    boxSizing: 'border-box',
    background: '#FAFAFA',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,39,68,0.65)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <div
        data-testid="employer-onboarding-modal"
        style={{ background: '#FFFFFF', borderRadius: '24px', padding: '36px', width: '100%', maxWidth: '440px', boxShadow: '0 32px 80px rgba(15,39,68,0.22)', position: 'relative' }}>
        {/* Close */}
        <button
          onClick={onDismiss}
          data-testid="onboarding-dismiss-btn"
          style={{ position: 'absolute', top: '16px', right: '16px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: '4px' }}>
          <X size={18} />
        </button>

        {/* Progress dots */}
        <div style={{ display: 'flex', gap: '6px', marginBottom: '24px', justifyContent: 'center' }}>
          {[1, 2, 3, 4].map(i => (
            <div key={i} style={{ width: i <= step ? '24px' : '8px', height: '8px', borderRadius: '4px', background: i === step ? '#F97316' : i < step ? '#0F2744' : '#E2E8F0', transition: 'all 0.25s' }} />
          ))}
        </div>

        {/* Step 1: Org Name */}
        {step === 1 && (
          <>
            <div style={{ textAlign: 'center', marginBottom: '28px' }}>
              <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: 'rgba(249,115,22,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                <Building2 size={24} color="#F97316" />
              </div>
              <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '22px', color: '#0F2744', margin: '0 0 8px' }}>
                Join the Talent Marketplace
              </h2>
              <p style={{ fontSize: '14px', color: '#64748B', margin: 0, lineHeight: 1.5 }}>
                Connect with credential-verified, workforce-ready candidates.
              </p>
            </div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', letterSpacing: '0.06em' }}>
              ORGANIZATION NAME
            </label>
            <input
              value={form.org_name}
              onChange={e => setForm(f => ({ ...f, org_name: e.target.value }))}
              placeholder="TechForce Solutions, Inc."
              data-testid="onboard-org-name"
              style={inputStyle}
            />
            <button
              onClick={() => setStep(2)}
              disabled={!canNext1}
              data-testid="onboard-next-1"
              style={{ marginTop: '24px', width: '100%', background: '#0F2744', border: 'none', borderRadius: '12px', padding: '14px', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '15px', color: '#FFFFFF', cursor: canNext1 ? 'pointer' : 'not-allowed', opacity: canNext1 ? 1 : 0.5 }}>
              Continue →
            </button>
          </>
        )}

        {/* Step 2: Commitment Level */}
        {step === 2 && (
          <>
            <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '20px', color: '#0F2744', margin: '0 0 8px' }}>
              Your Commitment
            </h2>
            <p style={{ fontSize: '14px', color: '#64748B', margin: '0 0 20px', lineHeight: 1.5 }}>
              How does your organization participate in second-chance hiring?
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {COMMITMENT_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setForm(f => ({ ...f, commitment_level: opt.value }))}
                  data-testid={`commitment-${opt.value}`}
                  style={{
                    background: form.commitment_level === opt.value ? 'rgba(15,39,68,0.06)' : '#FAFAFA',
                    border: `1.5px solid ${form.commitment_level === opt.value ? '#0F2744' : '#E2E8F0'}`,
                    borderRadius: '10px',
                    padding: '14px 16px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    textAlign: 'left',
                    transition: 'all 0.15s',
                  }}>
                  <div style={{ width: '18px', height: '18px', borderRadius: '50%', border: `2px solid ${form.commitment_level === opt.value ? '#0F2744' : '#CBD5E1'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {form.commitment_level === opt.value && <div style={{ width: '9px', height: '9px', borderRadius: '50%', background: '#0F2744' }} />}
                  </div>
                  <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '14px', color: '#0F2744' }}>{opt.label}</span>
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '24px' }}>
              <button onClick={() => setStep(1)} style={{ flex: 1, background: 'transparent', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '12px', cursor: 'pointer', color: '#475569', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600 }}>Back</button>
              <button onClick={() => setStep(3)} disabled={!canNext2} data-testid="onboard-next-2" style={{ flex: 2, background: '#0F2744', border: 'none', borderRadius: '12px', padding: '12px', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '14px', color: '#FFFFFF', cursor: canNext2 ? 'pointer' : 'not-allowed', opacity: canNext2 ? 1 : 0.5 }}>
                Continue →
              </button>
            </div>
          </>
        )}

        {/* Step 3: Credential Types */}
        {step === 3 && (
          <>
            <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '20px', color: '#0F2744', margin: '0 0 8px' }}>
              Preferred Credentials
            </h2>
            <p style={{ fontSize: '14px', color: '#64748B', margin: '0 0 20px', lineHeight: 1.5 }}>
              Which credential types best match your open roles?
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {CREDENTIAL_TYPES.map(c => {
                const selected = form.preferred_credentials.includes(c);
                return (
                  <button
                    key={c}
                    onClick={() => toggleCred(c)}
                    data-testid={`cred-type-${c.toLowerCase()}`}
                    style={{
                      background: selected ? '#0F2744' : '#F8FAFC',
                      border: `1.5px solid ${selected ? '#0F2744' : '#E2E8F0'}`,
                      borderRadius: '50px',
                      padding: '9px 18px',
                      cursor: 'pointer',
                      fontFamily: 'Plus Jakarta Sans, sans-serif',
                      fontWeight: 700,
                      fontSize: '13px',
                      color: selected ? '#FFFFFF' : '#475569',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'all 0.15s',
                    }}>
                    {selected && <CheckCircle size={12} />}
                    {c}
                  </button>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '24px' }}>
              <button onClick={() => setStep(2)} style={{ flex: 1, background: 'transparent', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '12px', cursor: 'pointer', color: '#475569', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600 }}>Back</button>
              <button onClick={() => setStep(4)} disabled={!canNext3} data-testid="onboard-next-3" style={{ flex: 2, background: '#0F2744', border: 'none', borderRadius: '12px', padding: '12px', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '14px', color: '#FFFFFF', cursor: canNext3 ? 'pointer' : 'not-allowed', opacity: canNext3 ? 1 : 0.5 }}>
                Continue →
              </button>
            </div>
          </>
        )}

        {/* Step 4: Contact */}
        {step === 4 && (
          <>
            <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '20px', color: '#0F2744', margin: '0 0 8px' }}>
              Your Contact
            </h2>
            <p style={{ fontSize: '14px', color: '#64748B', margin: '0 0 20px', lineHeight: 1.5 }}>
              Case managers will use this to reach your hiring team.
            </p>
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', letterSpacing: '0.06em' }}>CONTACT NAME</label>
              <input value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} placeholder="Jane Smith" data-testid="onboard-contact-name" style={inputStyle} />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', letterSpacing: '0.06em' }}>CONTACT EMAIL</label>
              <input type="email" value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} placeholder="jane@company.com" data-testid="onboard-contact-email" style={inputStyle} />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setStep(3)} style={{ flex: 1, background: 'transparent', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '12px', cursor: 'pointer', color: '#475569', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600 }}>Back</button>
              <button onClick={handleSubmit} disabled={!canSubmit || submitting} data-testid="onboard-submit-btn" style={{ flex: 2, background: '#F97316', border: 'none', borderRadius: '12px', padding: '12px', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '14px', color: '#0F2744', cursor: canSubmit && !submitting ? 'pointer' : 'not-allowed', opacity: canSubmit && !submitting ? 1 : 0.5 }}>
                {submitting ? 'Saving…' : 'Join Marketplace →'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
